import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { FeatureFlag } from '@mews-distributor/common';
import { AgeCategoryClassification } from '@mews-distributor/data-access';
import { createAction } from '@mews/framework';
import { DEFAULT_LANGUAGE_CODE } from '../../../constants';
import { PRICING_WITH_OCCUPANCY_FETCHED } from '../../../middlewares/gtmActions';
import { getValidQueryParamAvailabilityBlockId } from '../../../selectors/views/availabilityBlocks';
import { ApplicationState } from '../../../types';
import { DistributorPromiseThunkAction } from '../../../types/redux';
import { getLanguageCode } from '../../app/selectors';
import { getCategoriesOccupancyData } from '../../occupancy/categoriesOccupancies';
import { OccupancyData } from '../../resourceOrder/occupancyTypes';
import {
    getEnterpriseAvailabilityCategoryIds,
    hasIntervalAvailableCategories,
} from '../../serviceAvailability/selectors';
import { fetchServicePricing, ServicesGetPricingResponse } from '../../servicePricing';
import { getAgeCategoryClassification } from '../ageCategories/selectors';
import { getEnterpriseConfigurationId } from '../enterprises/selectors';
import { Enterprise } from '../enterprises/types';
import { getEnterpriseBasicProductIds } from '../products/selectors';
import { getEnterpriseServiceId } from './selectors';

interface FetchEnteprisePricingOptions {
    currencyCode: string;
    enterprise: Enterprise;
    /** YYYY-MM-DD */
    startDate: string;
    /** YYYY-MM-DD */
    endDate: string;
    voucherCode: string | undefined;
    startTimeUtc: string | undefined;
    endTimeUtc: string | undefined;
}

export const fetchEnterprisePricing =
    ({
        currencyCode,
        enterprise,
        startDate,
        endDate,
        voucherCode,
        startTimeUtc,
        endTimeUtc,
    }: FetchEnteprisePricingOptions): DistributorPromiseThunkAction<ServicesGetPricingResponse | null> =>
    async (dispatch, getState, { appClockService, featureFlagService }) => {
        const state = getState();
        const enterpriseId = enterprise.id;

        // TODO: implement logic for overnight bookings
        const isOvernightBookingEnabled = featureFlagService.hasFlag(FeatureFlag.overMidnightHourlyBookingsEnabled);

        const hasAvailableCategories = hasIntervalAvailableCategories(getState(), {
            enterpriseId: enterprise.id,
            startDate,
            endDate,
            startTimeUtc,
            endTimeUtc,
            isOvernightBookingEnabled,
        });

        /** We shouldn't call services/getPricing with occupied ( zero ) availability, because service pricing endpoint
         * doesn't care about availability and will return all restrictions, that would be applied when there would be an availability. */
        if (!hasAvailableCategories) {
            return null;
        }
        const serviceId = getEnterpriseServiceId(state, { enterpriseId });
        const configurationId = getEnterpriseConfigurationId(state, { enterpriseId });

        if (serviceId == null) {
            throw new TypeError('Service id is null');
        }

        if (configurationId == null) {
            throw new TypeError('Configuration id is null');
        }

        const nowUtc = appClockService.getAppNowUtc();
        const availabilityBlockId = getValidQueryParamAvailabilityBlockId(state, { nowUtc });

        // We don't have to check if these are empty because we already checked if there are some available categories with `hasAvailableCategories` above.
        const categoryIds = getEnterpriseAvailabilityCategoryIds(state, { enterpriseId });
        const occupancyData = getCategoriesOccupancyData(state, { serviceId });

        dispatch(createAction(PRICING_WITH_OCCUPANCY_FETCHED, mapClassificationsCount(state, occupancyData)));

        return dispatch(
            fetchServicePricing({
                availabilityBlockId,
                categoryIds,
                currencyCode,
                startDate,
                enterpriseId,
                languageCode: getLanguageCode(state) ?? DEFAULT_LANGUAGE_CODE,
                occupancyData,
                productIds: getEnterpriseBasicProductIds(state, { enterpriseId }),
                serviceId,
                configurationId,
                endDate,
                voucherCode,
                startTimeUtc,
                endTimeUtc,
            })
        );
    };

const mapClassificationsCount = (state: ApplicationState, occupancyData: OccupancyData[]) =>
    pipe(
        occupancyData,
        array.reduce({ adultCount: 0, childCount: 0 }, (accumulator, { ageCategoryId, personCount }) => {
            const classification = getAgeCategoryClassification(state, { ageCategoryId });
            if (classification === AgeCategoryClassification.Child) {
                accumulator.childCount += personCount;
            } else {
                accumulator.adultCount += personCount;
            }

            return accumulator;
        })
    );
